/**
 * CalendarReconnectBanner (FT-CAL-2) — the app-wide reconnect banner shown when the
 * CURRENT user's calendar grant is expired (the drain's auth-fatal flip). Rendered from
 * the reserved HeaderBanner slot (app top, all pages). Self-gating: renders nothing unless
 * there's an instanceId AND status==='expired' AND it hasn't been dismissed this session.
 *
 * Consumes the SAME getCalendarStatus query as IntegratedCalendarPanel, so RTK Query dedupes
 * (one request). Dismiss is in-memory (session-dismissible): it hides for the rest of this
 * SPA session but REAPPEARS on a full reload, since an expired grant is a standing problem
 * until the user reconnects. role="alert" (sync has actually stopped — actionable).
 */
import { useState } from "react";
import { X } from "lucide-react";
import { getInstanceIdFromUrl } from "../../utils/instanceId";
import { usePacks } from "../packs/PackContext";
import { useGetCalendarStatusQuery } from "../../store/api/crmCalendarApi";

export function CalendarReconnectBanner() {
  const instanceId = getInstanceIdFromUrl() ?? "";
  const [dismissed, setDismissed] = useState(false);

  // Gate on the SAME pack+mode as the profile entry: only an operator-pack user actively in
  // CRM mode can hold a calendar grant, so only they should fire the status read.
  const { hasOperatorPack, currentMode, isLoading: packsLoading } = usePacks();
  const gated = !packsLoading && hasOperatorPack && currentMode === "operator_crm";

  const { data: status } = useGetCalendarStatusQuery(
    { instanceId },
    { skip: !gated || !instanceId, refetchOnMountOrArgChange: true },
  );

  if (!gated || !instanceId || dismissed || status?.status !== "expired") return null;

  const profileHref = `/crm/profile?section=calendar&instanceId=${encodeURIComponent(instanceId)}`;

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <span>
        Your calendar sync has stopped.{" "}
        <a
          href={profileHref}
          className="font-semibold underline underline-offset-2 hover:text-amber-700"
        >
          Reconnect
        </a>{" "}
        to resume.
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="ml-1 rounded p-0.5 text-amber-700 transition hover:bg-amber-100 hover:text-amber-900"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
